import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { composeClassName } from '@/utils/util_helpers';
import { useWindowDimensions } from '@/hooks/use-window-dimensions';
import NavButton from '@/components/design-system/atoms/nav-button';

interface FloorPlan {
  src: string;
  alt: string;
}

interface FloorPlanViewerProps {
  className?: string;
  floorPlans: FloorPlan[];
}

const FloorPlanViewer: React.FC<FloorPlanViewerProps> = ({ className, floorPlans }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();
  const { screenIsLargeOrEqualThan } = useWindowDimensions();
  const isMobile = !screenIsLargeOrEqualThan('sm');

  useEffect(() => {
    if (!modalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setModalOpen(false);
      } else if (e.key === 'ArrowLeft') {
        setCurrentIndex(prevIndex => (prevIndex === 0 ? floorPlans.length - 1 : prevIndex - 1));
      } else if (e.key === 'ArrowRight') {
        setCurrentIndex(prevIndex => (prevIndex === floorPlans.length - 1 ? 0 : prevIndex + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, floorPlans.length]);

  if (!floorPlans?.length) {
    return null;
  }

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? floorPlans.length - 1 : prevIndex - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex === floorPlans.length - 1 ? 0 : prevIndex + 1));
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
  };

  const handleImageClick = () => {
    if (!isMobile) {
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <div style={{ borderColor: '#E8E8E8' }} className="border-t border-solid pt-7">
      <h2
        className={composeClassName(
          'font-sofia-pro mb-4 mt-4 font-bold leading-[30px] text-N900',
          isMobile ? 'text-lead1' : 'text-[22px]'
        )}
      >
        {t('js.components.house_tabs.floor_plan')}
      </h2>
      <div className={composeClassName(className)}>
        <div
          className="rounded-2xl relative mb-4 cursor-pointer overflow-hidden border border-gray-200"
          onClick={handleImageClick}
        >
          <img
            src={floorPlans[currentIndex].src}
            alt={floorPlans[currentIndex].alt}
            className="w-full object-contain"
          />
        </div>

        {floorPlans.length > 1 && (
          <div className="relative flex items-center">
            <NavButton
              direction="left"
              onClick={handlePrevious}
              className="absolute left-0"
              ariaLabel="Previous floor plan"
              iconStrokeWidth={2}
            />

            <div className="flex w-full gap-4 overflow-x-auto px-10 py-2">
              {floorPlans.map((plan, index) => (
                <button
                  key={index}
                  onClick={() => handleThumbnailClick(index)}
                  style={{
                    height: isMobile ? '56px' : '167px',
                    width: isMobile ? '56px' : '273px',
                  }}
                  className={composeClassName(
                    'flex-shrink-0 overflow-hidden rounded-lg',
                    index === currentIndex ? 'border border-black' : ''
                  )}
                >
                  <img src={plan.src} alt={plan.alt} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>

            <NavButton
              direction="right"
              onClick={handleNext}
              className="absolute right-0"
              ariaLabel="Next floor plan"
              iconStrokeWidth={2}
            />
          </div>
        )}
      </div>

      {!isMobile && modalOpen && (
        <div
          className="fixed inset-0 z-[2000] cursor-zoom-out bg-black bg-opacity-90"
          onClick={handleCloseModal}
        >
          <div className="relative flex h-full w-full items-center justify-center">
            <NavButton
              direction="left"
              onClick={(e) => setCurrentIndex(currentIndex === 0 ? floorPlans.length - 1 : currentIndex - 1)}
              className="absolute left-4"
              ariaLabel="Previous floor plan"
              iconStrokeWidth={2}
              stopPropagation
            />
            <img
              src={floorPlans[currentIndex].src}
              alt={floorPlans[currentIndex].alt}
              className="max-h-[90vh] max-w-[90vw] cursor-default select-none object-contain"
              draggable={false}
              onClick={(e) => e.stopPropagation()}
            />
            <NavButton
              direction="right"
              onClick={(e) => setCurrentIndex(currentIndex === floorPlans.length - 1 ? 0 : currentIndex + 1)}
              className="absolute right-4"
              ariaLabel="Next floor plan"
              iconStrokeWidth={2}
              stopPropagation
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FloorPlanViewer;
